import { C } from '../palette';
import { PixelBuf } from '../pixel';

/** Background scenery. Purely decorative: never collides. */
export function buildDecorArt(): Record<string, PixelBuf> {
  return {
    'hill.big': hill(64, 40),
    'hill.small': hill(40, 26),
    'cloud.big': cloud(48, 24),
    'cloud.small': cloud(32, 18),
    'bush.big': bush(48, 20),
    'bush.small': bush(28, 16),
    fence: fence(),
    castle: castle(),
    stalactite: stalactite(),
    cluster: cluster(),
    'mush.big': mushroomTree(40, 34),
    'mush.small': mushroomTree(26, 24),
  };
}

function hill(w: number, h: number): PixelBuf {
  const p = new PixelBuf(w, h);
  // rounded mound: narrow at the top, widest along the ground
  for (let y = 0; y < h; y++) {
    const half = Math.round((w / 2) * Math.sqrt((y + 1) / h) * 1.02);
    const x = Math.round(w / 2 - half);
    p.rect(x, y, half * 2, 1, C.hill);
    p.rect(x, y, 2, 1, C.hillDark);
    p.rect(x + half * 2 - 2, y, 2, 1, C.hillDark);
  }
  // highlight cap + eye-like dimples (original motif)
  p.rect(w / 2 - 4, 2, 8, 2, C.hillLight);
  p.rect(w / 2 - 8, h - 12, 4, 3, C.hillDark);
  p.rect(w / 2 + 4, h - 12, 4, 3, C.hillDark);
  p.rect(w / 2 - 2, h - 8, 4, 2, C.hillDark);
  return p;
}

function cloud(w: number, h: number): PixelBuf {
  const p = new PixelBuf(w, h);
  const lobes: [number, number, number][] = [
    [0.16, 0.62, 0.3],
    [0.42, 0.36, 0.4],
    [0.7, 0.6, 0.32],
  ];
  for (const [cx, cy, r] of lobes) {
    const rw = Math.round(w * r);
    const rh = Math.round(h * r * 1.5);
    p.blob(Math.round(w * cx), Math.round(h * cy) - rh, rw, rh + 4, C.cloud);
  }
  p.rect(2, h - 5, w - 4, 4, C.cloud);
  p.rect(3, h - 2, w - 6, 1, C.cloudShade);
  p.rect(6, h - 8, w - 14, 2, C.cloudShade);
  return p;
}

function bush(w: number, h: number): PixelBuf {
  const p = new PixelBuf(w, h);
  const lobes: [number, number][] = [
    [0.1, 0.55],
    [0.36, 0.85],
    [0.66, 0.6],
  ];
  for (const [cx, s] of lobes) {
    const rw = Math.round(w * 0.34);
    const rh = Math.round(h * s);
    p.blob(Math.round(w * cx), h - rh, rw, rh, C.bush);
  }
  p.rect(0, h - 3, w, 3, C.bush);
  p.rect(0, h - 1, w, 1, C.bushDark);
  p.rect(4, h - 8, 5, 2, C.hillLight);
  return p;
}

function fence(): PixelBuf {
  const p = new PixelBuf(32, 16);
  p.rect(0, 4, 32, 3, C.mortar);
  p.rect(0, 10, 32, 3, C.mortar);
  for (let x = 2; x < 32; x += 8) {
    p.rect(x, 0, 4, 16, C.mortar);
    p.rect(x + 3, 0, 1, 16, C.stoneDark);
  }
  p.rect(0, 6, 32, 1, C.stoneDark);
  p.rect(0, 12, 32, 1, C.stoneDark);
  return p;
}

/** Finish castle, 80x80 (drawn behind the goal). */
function castle(): PixelBuf {
  const p = new PixelBuf(80, 80);
  const wall = (x: number, y: number, w: number, h: number) => {
    p.rect(x, y, w, h, C.brickDark);
    for (let yy = y; yy < y + h; yy += 4) {
      const off = ((yy - y) / 4) % 2 === 0 ? 0 : 4;
      for (let xx = x - 4; xx < x + w; xx += 8) {
        const bx = Math.max(x, xx + off);
        const bw = Math.min(7, x + w - bx);
        if (bw > 0) {
          p.rect(bx, yy, bw, 3, C.brick);
          p.rect(bx, yy, bw, 1, C.brickLight);
        }
      }
    }
  };
  const battlement = (x: number, y: number, w: number) => {
    for (let xx = x; xx < x + w; xx += 8) {
      p.rect(xx, y, 4, 4, C.brick);
      p.rect(xx, y, 4, 1, C.brickLight);
    }
    p.rect(x, y + 4, w, 2, C.brickDark);
  };

  wall(8, 30, 64, 50);
  battlement(8, 24, 64);
  // towers
  wall(0, 42, 16, 38);
  battlement(0, 36, 16);
  wall(64, 42, 16, 38);
  battlement(64, 36, 16);
  // keep
  wall(30, 8, 20, 24);
  battlement(30, 2, 20);
  // gate
  p.blob(32, 52, 16, 28, C.black);
  p.rect(34, 56, 12, 24, '#1b1630');
  // windows
  for (const [wx, wy] of [
    [20, 40],
    [56, 40],
    [37, 16],
  ] as [number, number][]) {
    p.rect(wx, wy, 5, 7, C.black);
    p.rect(wx + 1, wy + 1, 3, 5, '#2a2050');
  }
  return p;
}

function stalactite(): PixelBuf {
  const p = new PixelBuf(16, 26);
  for (let y = 0; y < 26; y++) {
    const w = Math.max(1, Math.round(10 * (1 - y / 26)));
    p.rect(8 - Math.ceil(w / 2), y, w, 1, C.cavesoil);
    p.px(8 - Math.ceil(w / 2), y, C.cavesoilLight);
  }
  return p;
}

function cluster(): PixelBuf {
  const p = new PixelBuf(24, 20);
  const shard = (x: number, base: number, h: number, w: number) => {
    for (let y = 0; y < h; y++) {
      const ww = Math.max(1, Math.round(w * (1 - y / h)));
      p.rect(x - Math.floor(ww / 2), base - y, ww, 1, y > h * 0.6 ? C.crystalLight : C.crystal);
    }
  };
  shard(6, 19, 14, 6);
  shard(13, 19, 19, 7);
  shard(19, 19, 10, 5);
  return p;
}

/** Cave mushroom tree used as a platform decoration backdrop. */
function mushroomTree(w: number, h: number): PixelBuf {
  const p = new PixelBuf(w, h);
  const stemW = Math.max(4, Math.round(w * 0.22));
  p.rect((w - stemW) / 2, h * 0.4, stemW, h * 0.6, C.cavesoilLight);
  p.rect((w - stemW) / 2, h * 0.4, 2, h * 0.6, C.cavesoil);
  const capH = Math.round(h * 0.45);
  p.blob(0, 0, w, capH, C.nipperDark);
  p.blob(1, 1, w - 2, capH - 2, C.nipper);
  p.rect(4, 3, 6, 3, C.nipperLight);
  p.rect(w - 12, 5, 5, 3, C.nipperLight);
  return p;
}
